import fuzzysort from 'fuzzysort';
import _ from 'lodash';
import {DateTime} from 'luxon';
import uuid from 'uuid-random';

import {ErrorWithData} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkModel,
} from '../../models/api/schedulingLinkModel';
import {
  isSelectionQuestion,
  SchedulingLinkFreeformQuestionModel,
  SchedulingLinkQuestionModel,
  SchedulingLinkQuestionTypesEnum,
  SchedulingLinkSelectionOptionModel,
  SchedulingLinkSelectionQuestionModel,
} from '../../models/api/schedulingLinkQuestionModel';

/*
 * Constants.
 */

export const schedulingLinkAdvanceNoticeOptions = [
  0, 15, 30, 45, 60, 120, 180, 360, 720, 1440, 2880, 4320, 7200, 10080,
];

export const schedulingLinkStartTimeIncrements = [15, 20, 30, 60];

export const schedulingLinkMeetingBufferOptions = [0, 5, 10, 15, 20, 30, 60, 120, 180];

export const schedulingLinkMeetingDurationOptions = [
  15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 75, 90, 105, 120, 135, 150, 165, 180, 240, 300, 360, 420, 480, 540,
  600, 660, 720,
];

export enum SettingsSectionsEnum {
  AVAILABILITY = 'availability',
  MEETING = 'meeting',
  URL = 'url',
  APPEARANCE = 'appearance',
}

export enum SchedulingLinkPropertiesEnum {
  DOUBLE_BOOKING,
  ADDITIONAL_GUESTS,
  MEETING_BUFFER,
  ADVANCE_NOTICE,
  START_TIME_INCREMENTS,
  TITLE,
  LOCATION,
  DESCRIPTION,
  GREETING,
  DISPLAY_NAME,
}

const fuzzySortOptions = {
  allowTypo: true,
  threshold: -1000,
  keys: ['name', 'meetingProperties.summary', 'meetingProperties.description'],
};

export enum SlugErrorsEnum {
  DUPLICATE = 'duplicate',
  INVALID_CONTENT = 'invalid_content',
}

/*
 * Helpers.
 */

export function createFreeformQuestion(uid?: string): SchedulingLinkFreeformQuestionModel {
  return {
    uid: uid || uuid(),
    type: SchedulingLinkQuestionTypesEnum.FREEFORM,
    question: '',
  };
}

export function createSelectionQuestion(uid?: string): SchedulingLinkSelectionQuestionModel {
  return {
    uid: uid || uuid(),
    type: SchedulingLinkQuestionTypesEnum.SELECTION,
    question: '',
    selectionOptions: [createSelectionOption(), createSelectionOption()],
  };
}

export function createSelectionOption(): SchedulingLinkSelectionOptionModel {
  return {
    uid: uuid(),
    value: '',
  };
}

export function filterBySearchQuery(schedulingLinks: ReadonlyArray<SchedulingLinkModel>, query: string) {
  return fuzzysort
    .go(query, schedulingLinks as Array<SchedulingLinkModel>, fuzzySortOptions)
    .map((result) => result.obj);
}

export function buildSlugError(slug: string, otherSlugs: ReadonlyArray<string>) {
  const isUnique = otherSlugs.every((otherSlug) => slug !== otherSlug);
  if (!isUnique) {
    return SlugErrorsEnum.DUPLICATE;
  }

  if (!/^[a-zA-Z0-9-_]*$/.test(slug) || slug.length > 25) {
    return SlugErrorsEnum.INVALID_CONTENT;
  }

  return null;
}

export function buildInvalidQuestionOptionUids(questions: ReadonlyArray<SchedulingLinkQuestionModel>) {
  return (
    _(questions)
      // Only validate selection questions.
      .filter(isSelectionQuestion)
      // Verify question is filled out.
      .filter((question) => Boolean(question.question))
      // Take questions with less than 2 options filled out.
      .filter((question) => question.selectionOptions.filter((option) => Boolean(option.value)).length < 2)
      // Get first empty selection option.
      .map((question) => question.selectionOptions.find((option) => !option.value))
      .compact()
      // Get the option uid.
      .map((option) => option.uid)
      .value()
  );
}

/** Do a client side check for scheduling link availability. */
export function isSchedulingLinkAvailable(link: DraftSchedulingLinkModel | SchedulingLinkModel) {
  const meetingDurationMs = link.meetingProperties.durationMinutes * 60 * 1000;

  if (link.availability.type === SchedulingLinkAvailabilityTypesEnum.STANDARD) {
    // For standard links, check if the duration of a block is larger than the meeting duration.
    return link.availability.blocks.some((slot) => slot.end - slot.start >= meetingDurationMs);
  }

  if (link.availability.type === SchedulingLinkAvailabilityTypesEnum.REPEATING) {
    // For recurring links, check if a block exists that is longer than the meeting duration.
    return link.availability.blocks.some((slot) => slot.durationMinutes * 60 * 1000 >= meetingDurationMs);
  }

  // @ts-expect-error: This is theoretically unreachable, but check just in case.
  reportToBugsnag(new ErrorWithData('Unknown availability type', {type: link.availabilityType}));
  return false;
}

export function isSchedulingLinkOutdated(schedulingLink: DraftSchedulingLinkModel | SchedulingLinkModel) {
  if (schedulingLink.availability.type === SchedulingLinkAvailabilityTypesEnum.REPEATING) {
    return false;
  }

  if (schedulingLink.availability.type === SchedulingLinkAvailabilityTypesEnum.STANDARD) {
    const now = DateTime.now().valueOf();
    return schedulingLink.availability.blocks.every((block) => block.end < now);
  }

  return true;
}
